import { useEffect, useRef, useState } from "react";
import { Button } from "./ui";

/**
 * Camera scanning for tablets and phones. Uses the browser's built-in BarcodeDetector where it
 * exists (Chrome on Android), otherwise ZXing (loaded only when the camera is opened). Calls
 * `onCode` for each code it reads; the same code is ignored for 2.5 s so one box isn't added twice.
 */
type Detector = { detect: (src: CanvasImageSource) => Promise<{ rawValue: string }[]> };

export function CameraScanner({ onCode, onClose, title = "Scan with the camera", hint = "Barcode, QR or IMEI label inside the frame", children }: { onCode: (code: string) => void; onClose: () => void; title?: string; hint?: string; children?: React.ReactNode }) {
  const video = useRef<HTMLVideoElement>(null);
  const [err, setErr] = useState("");
  const [torch, setTorch] = useState(false);
  const [facing, setFacing] = useState<"environment" | "user">("environment");
  const lastCode = useRef<{ code: string; at: number }>({ code: "", at: 0 });
  const cb = useRef(onCode);
  cb.current = onCode;
  const stream = useRef<MediaStream | null>(null);

  useEffect(() => {
    let stopped = false;
    let stopZxing: (() => void) | null = null;
    let raf = 0;
    const emit = (code: string) => {
      const now = Date.now();
      if (code === lastCode.current.code && now - lastCode.current.at < 2500) return;
      lastCode.current = { code, at: now };
      cb.current(code.trim());
    };
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
        if (stopped) return s.getTracks().forEach((t) => t.stop());
        stream.current = s;
        const v = video.current!;
        v.srcObject = s;
        await v.play();
        const BD = (window as unknown as { BarcodeDetector?: new (o?: { formats: string[] }) => Detector }).BarcodeDetector;
        if (BD) {
          const det = new BD({ formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "code_93", "itf", "qr_code", "data_matrix"] });
          const loop = async () => {
            if (stopped) return;
            try {
              const found = await det.detect(v);
              if (found[0]?.rawValue) emit(found[0].rawValue);
            } catch {
              /* frame not ready */
            }
            raf = window.setTimeout(loop, 180) as unknown as number;
          };
          void loop();
        } else {
          const { BrowserMultiFormatReader } = await import("@zxing/browser");
          const reader = new BrowserMultiFormatReader();
          const controls = await reader.decodeFromVideoElement(v, (res) => {
            if (res) emit(res.getText());
          });
          stopZxing = () => controls.stop();
        }
      } catch (e) {
        setErr(e instanceof DOMException && e.name === "NotAllowedError" ? "Camera blocked. Allow camera access for this site in the browser settings." : "No camera found on this device.");
      }
    })();
    return () => {
      stopped = true;
      clearTimeout(raf);
      stopZxing?.();
      stream.current?.getTracks().forEach((t) => t.stop());
      stream.current = null;
    };
  }, [facing]);

  const toggleTorch = async () => {
    const track = stream.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch } as MediaTrackConstraintSet] });
      setTorch(!torch);
    } catch {
      setErr("This camera has no torch.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900 text-white lg:flex-row">
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="flex h-16 flex-none items-center gap-3 px-4">
          <button type="button" onClick={onClose} className="h-10 px-3 text-sm font-semibold hover:bg-slate-800">
            ← Done
          </button>
          <b className="text-sm">{title}</b>
          <div className="ml-auto flex gap-2">
            <Button size="sm" tone="dark" onClick={toggleTorch} className="!border-slate-700 !bg-slate-800">
              Torch
            </Button>
            <Button size="sm" tone="dark" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} className="!border-slate-700 !bg-slate-800">
              Flip
            </Button>
          </div>
        </div>
        <div className="relative grid min-h-0 flex-1 place-items-center overflow-hidden bg-black">
          <video ref={video} playsInline muted className="absolute inset-0 h-full w-full object-cover" />
          <div className="relative h-[34%] w-[78%] max-w-xl border-[3px] border-teal-300">
            <div className="absolute inset-x-0 top-1/2 h-0.5 bg-amber-400" />
          </div>
          <span className="absolute bottom-6 bg-slate-900/70 px-3 py-1.5 text-xs">{err || hint}</span>
        </div>
      </div>
      {children ? <div className="max-h-[45vh] w-full overflow-y-auto bg-white text-slate-900 lg:max-h-none lg:w-[420px]">{children}</div> : null}
    </div>
  );
}
